import { useEffect, useRef, useState } from "react";
import { useInView, useMotionValue, useTransform, animate } from "framer-motion";

/**
 * Animated number counter that ramps from 0 to `value` once on viewport enter.
 * - Accepts numeric `value` (preferred) for clean tween, OR a preformatted string
 *   like "2.053" — in that case we strip non-digits, animate the integer, then
 *   re-format with locale separators.
 * - `prefix` and `suffix` are rendered as static text around the number.
 */
export default function NumberCounter({
  value,
  duration = 1.4,
  prefix = "",
  suffix = "",
  className = "",
  locale = "it-IT",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  // Normalize value to a plain integer for the tween
  const target =
    typeof value === "number"
      ? value
      : parseInt(String(value).replace(/[^\d-]/g, ""), 10) || 0;

  const motionVal = useMotionValue(0);
  const formatted = useTransform(motionVal, (latest) => {
    const n = Math.round(latest);
    // Force grouping so 4-digit numbers like 2053 always render as "2.053"
    // across browser ICU variants (some headless builds collapse 'auto' to no-group).
    return n.toLocaleString(locale, { useGrouping: "always" });
  });

  const [display, setDisplay] = useState("0");

  useEffect(() => {
    return formatted.on("change", (v) => setDisplay(v));
  }, [formatted]);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(motionVal, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
    });
    return () => controls.stop();
  }, [inView, target, duration, motionVal]);

  return (
    <span
      ref={ref}
      className={`font-numeric ${className}`}
      aria-label={`${prefix}${target}${suffix}`}
    >
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
